import React, { useState } from 'react';
import { deploymentRolePolicy } from '../Core/AwsDeploymentPolicy';
import { AwsService } from '../Service/AwsService';

interface AWSSetupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  externalId: string;
}

// ── Permission result types ───────────────────────────────────────────────────

interface PermissionResult {
  action: string;
  group: string;
  allowed: boolean;
  decision: string;
}

interface VerifyResponse {
  success: boolean;
  message: string;
  role_arn: string;
  region: string;
  account_id?: string;
  permissions: PermissionResult[];
  missing_permissions: string[];
  remarks: string[];
  verified_at: string;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: '100%', background: '#17171e', border: '1px solid #26262f',
  borderRadius: '6px', padding: '8px 11px', color: '#eeeef5',
  fontSize: '13px', fontFamily: "'Inter',sans-serif", outline: 'none',
  boxSizing: 'border-box' as const, transition: 'border-color .15s',
};

const lbl: React.CSSProperties = {
  fontSize: '12px', fontWeight: 500, color: '#8e8ea8',
  display: 'block', marginBottom: '5px',
};

const btn = (v: 'primary'|'secondary'|'ghost' = 'secondary'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s',
  ...(v === 'primary'  ? { background: '#6366f1', color: '#fff' }
    : v === 'ghost'    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid #26262f' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

// ── Copy button ───────────────────────────────────────────────────────────────

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} style={{ ...btn('ghost'), fontSize: '11.5px', padding: '4px 10px' }}>
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M1.5 6l3.5 3.5 5.5-6"/></svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="1" width="7" height="8" rx="1"/><path d="M1 4v7a1 1 0 001 1h6"/></svg>
      )}
      {copied ? 'Copied!' : label}
    </button>
  );
};

// ── Code block ────────────────────────────────────────────────────────────────

const CodeBlock: React.FC<{ content: string; copyLabel?: string }> = ({ content, copyLabel }) => (
  <div style={{ position: 'relative' as const, marginTop: '10px' }}>
    <pre style={{
      background: '#080810', border: '1px solid #26262f', borderRadius: '6px',
      padding: '12px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px',
      color: '#8080b0', overflowX: 'auto', margin: 0, lineHeight: 1.7,
      maxHeight: '200px', overflowY: 'auto',
    }}>
      {content}
    </pre>
    <div style={{ position: 'absolute' as const, top: '8px', right: '8px' }}>
      <CopyButton text={content} label={copyLabel} />
    </div>
  </div>
);

// ── Step indicator ────────────────────────────────────────────────────────────

const StepDot: React.FC<{ n: number; active: boolean; done: boolean }> = ({ n, active, done }) => (
  <div style={{
    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: 700,
    background: done ? '#22c55e' : active ? '#6366f1' : '#1e1e27',
    color: done || active ? '#fff' : '#52525f',
    border: done || active ? 'none' : '2px solid #32323d',
  }}>
    {done ? <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#fff" strokeWidth="2"><path d="M1.5 5.5l3 3 5-5"/></svg> : n}
  </div>
);

// ── Permission results panel ──────────────────────────────────────────────────

const PermissionPanel: React.FC<{ response: VerifyResponse }> = ({ response }) => {
  const groups: Record<string, PermissionResult[]> = {};
  for (const p of response.permissions) {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Summary */}
      <div style={{
        padding: '12px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px',
        background: response.success ? 'rgba(34,197,94,.08)' : 'rgba(244,63,94,.08)',
        border: `1px solid ${response.success ? 'rgba(34,197,94,.25)' : 'rgba(244,63,94,.25)'}`,
      }}>
        {response.success ? (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#22c55e" strokeWidth="1.5"><circle cx="9" cy="9" r="7.5"/><path d="M5 9l3.5 3.5 4.5-5"/></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#f43f5e" strokeWidth="1.5"><circle cx="9" cy="9" r="7.5"/><path d="M9 5.5v4M9 12v.5"/></svg>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: response.success ? '#22c55e' : '#f43f5e' }}>
            {response.message}
          </div>
          {response.account_id && (
            <div style={{ fontSize: '11.5px', color: '#52525f', marginTop: '2px', fontFamily: "'IBM Plex Mono',monospace" }}>
              Account: {response.account_id} · Region: {response.region}
            </div>
          )}
        </div>
      </div>

      {/* Remarks */}
      {response.remarks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {response.remarks.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '12px', color: '#8e8ea8', padding: '6px 10px', background: '#17171e', borderRadius: '5px', border: '1px solid #26262f' }}>
              <span style={{ color: response.success ? '#22c55e' : '#eab308', flexShrink: 0 }}>
                {response.success ? '✓' : '⚠'}
              </span>
              {r}
            </div>
          ))}
        </div>
      )}

      {/* Permission groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Object.entries(groups).map(([group, perms]) => {
          const allOk = perms.every(p => p.allowed);
          return (
            <div key={group} style={{ border: '1px solid #26262f', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                background: allOk ? 'rgba(34,197,94,.04)' : 'rgba(244,63,94,.04)',
                borderBottom: '1px solid #26262f',
              }}>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', flex: 1 }}>{group}</span>
                <span style={{
                  fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '20px',
                  background: allOk ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
                  color: allOk ? '#22c55e' : '#f43f5e',
                }}>
                  {perms.filter(p => p.allowed).length}/{perms.length} allowed
                </span>
              </div>
              <div style={{ padding: '6px 0' }}>
                {perms.map(p => (
                  <div key={p.action} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px' }}>
                    {p.allowed ? (
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M1.5 5.5l3 3 5-5"/></svg>
                    ) : (
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#f43f5e" strokeWidth="1.5"><path d="M2 2l7 7M9 2L2 9"/></svg>
                    )}
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: p.allowed ? '#8e8ea8' : '#f43f5e', flex: 1 }}>
                      {p.action}
                    </span>
                    {!p.allowed && (
                      <span style={{ fontSize: '10px', color: '#52525f' }}>{p.decision}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ── Main AWSSetupModal ────────────────────────────────────────────────────────

const AWSSetupModal: React.FC<AWSSetupModalProps> = ({ open, onClose, onSuccess, externalId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [roleArn, setRoleArn] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResponse, setVerifyResponse] = useState<VerifyResponse | null>(null);

  if (!open) return null;

  const policy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "arn:aws:iam::463470964985:user/StsUser"
      },
      "Action": "sts:AssumeRole",
      "Condition": {
        "StringEquals": {
          "sts:ExternalId": "${externalId}"
        }
      }
    },
    {
      "Effect": "Allow",
      "Principal": {
        "Service": "codebuild.amazonaws.com"
      },
      "Action": "sts:AssumeRole"
    },
    {
      "Effect": "Allow",
      "Principal": {
        "Service": "ecr.amazonaws.com"
      },
      "Action": "sts:AssumeRole"
    }
  ]
}`;

  const handleVerify = async () => {
    if (!roleArn) { setError('Role ARN is required'); return; }
    if (!region)  { setError('Region is required'); return; }
    setError(null);

    try {
      setIsConnecting(true);
      const clientResult = await AwsService.createCredentials({ role_arn: roleArn, region });
      setIsConnecting(false);

      if (!clientResult.success) {
        setError(clientResult.message || 'Failed to create AWS client');
        return;
      }

      setIsVerifying(true);
      const verifyResult = await AwsService.verifyConnection(roleArn);
      setIsVerifying(false);

      // verifyResult now contains the full VerifyResponse with permissions
      setVerifyResponse(verifyResult as any);
      setActiveStep(3); // move to results step

      if (verifyResult.success) {
        onSuccess?.();
      }
    } catch (err: any) {
      setIsConnecting(false);
      setIsVerifying(false);
      setError(err?.message || 'An unexpected error occurred');
    }
  };

  const regions = [
    { value: 'us-east-1',      label: 'US East (N. Virginia)' },
    { value: 'us-east-2',      label: 'US East (Ohio)' },
    { value: 'us-west-1',      label: 'US West (N. California)' },
    { value: 'us-west-2',      label: 'US West (Oregon)' },
    { value: 'eu-west-1',      label: 'EU (Ireland)' },
    { value: 'eu-central-1',   label: 'EU (Frankfurt)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-south-1',     label: 'Asia Pacific (Mumbai)' },
  ];

  const steps = [
    { label: 'Create Role & Trust Policy' },
    { label: 'Attach Permissions' },
    { label: 'Configure & Verify' },
  ];

  const isBusy = isConnecting || isVerifying;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
      onClick={onClose}>
      <div style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', width: '600px', maxWidth: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,.7)' }}
        onClick={e => e.stopPropagation()}>

        {/* Modal header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(255,153,0,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🟠</div>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5' }}>Connect AWS Account</div>
            <div style={{ fontSize: '12px', color: '#52525f' }}>3-step guided setup · ExternalId: <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#818cf8' }}>{externalId}</span></div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#52525f', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2l10 10M12 2L2 12"/></svg>
          </button>
        </div>

        {/* Step progress */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={() => activeStep !== 3 && setActiveStep(i)}>
                <StepDot n={i + 1} active={activeStep === i} done={activeStep > i || activeStep === 3} />
                <span style={{ fontSize: '10px', fontWeight: 500, color: activeStep === i ? '#818cf8' : activeStep > i ? '#22c55e' : '#52525f', whiteSpace: 'nowrap' as const }}>{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ flex: 1, height: '2px', margin: '0 6px', marginBottom: '14px', background: activeStep > i || activeStep === 3 ? '#22c55e' : '#32323d' }}/>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step content — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* Step 0 — Trust policy */}
          {activeStep === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>Create Role & Add Trust Policy</div>
              {[
                'In your AWS account, navigate to IAM Console → Roles → Create Role',
                'Select "Custom Trust Policy" as the trusted entity type',
                'Copy the trust policy below and paste it in AWS',
                "After pasting, don't add any permissions yet. Create the role with any name (e.g. QixDeployRole)",
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,153,0,.12)', color: '#ff9900', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <div style={{ fontSize: '12.5px', color: '#8e8ea8', lineHeight: 1.6, paddingTop: '1px' }}>{step}</div>
                </div>
              ))}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.06em', color: '#52525f', marginBottom: '6px' }}>Trust Relationship Policy</div>
                <CodeBlock content={policy} copyLabel="Copy Trust Policy" />
              </div>
              <div style={{ padding: '10px 12px', background: 'rgba(234,179,8,.06)', border: '1px solid rgba(234,179,8,.2)', borderRadius: '6px', fontSize: '12px', color: '#eab308', display: 'flex', gap: '7px' }}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/></svg>
                Make sure the Principal points to our StsUser, not the root account
              </div>
            </div>
          )}

          {/* Step 1 — Permissions policy */}
          {activeStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>Attach Permissions Policy</div>
              {[
                'Open your newly created role',
                'Click on "Add permissions" → "Create inline policy"',
                'In the policy editor, select JSON tab',
                'Paste the policy below',
                'Review and create the policy with a name like "QixDeployPolicy"',
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,153,0,.12)', color: '#ff9900', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <div style={{ fontSize: '12.5px', color: '#8e8ea8', lineHeight: 1.6, paddingTop: '1px' }}>{step}</div>
                </div>
              ))}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.06em', color: '#52525f', marginBottom: '6px' }}>Deployment Permission Policy</div>
                <CodeBlock content={JSON.stringify(deploymentRolePolicy, null, 2)} copyLabel="Copy Policy" />
              </div>
            </div>
          )}

          {/* Step 2 — Role ARN + region */}
          {activeStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>Configure Connection</div>
              {[
                'Copy the Role ARN from your newly created role',
                'The ARN should look like: arn:aws:iam::YOUR_ACCOUNT_ID:role/QixDeployRole',
                'Select the AWS region where you want to deploy resources',
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,153,0,.12)', color: '#ff9900', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <div style={{ fontSize: '12.5px', color: '#8e8ea8', lineHeight: 1.6, paddingTop: '1px' }}>{step}</div>
                </div>
              ))}

              <div>
                <label style={lbl}>Role ARN *</label>
                <input
                  style={{ ...inp, fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', borderColor: error && !roleArn ? '#f43f5e' : '#26262f' }}
                  placeholder="arn:aws:iam::123456789012:role/QixDeployRole"
                  value={roleArn}
                  onChange={e => setRoleArn(e.target.value)}
                  onFocus={e => (e.target.style.borderColor = '#6366f1')}
                  onBlur={e => (e.target.style.borderColor = error && !roleArn ? '#f43f5e' : '#26262f')}
                />
              </div>

              <div>
                <label style={lbl}>AWS Region *</label>
                <select
                  value={region}
                  onChange={e => setRegion(e.target.value)}
                  style={{ ...inp, cursor: 'pointer', appearance: 'none' as const, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none' stroke='%2352525f' stroke-width='1.5'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '30px' }}
                >
                  {regions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              {error && (
                <div style={{ padding: '10px 12px', borderRadius: '6px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)', color: '#f43f5e', fontSize: '12.5px', display: 'flex', gap: '7px' }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/></svg>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Permission results */}
          {activeStep === 3 && verifyResponse && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>Verification Results</div>
              <PermissionPanel response={verifyResponse} />
              {!verifyResponse.success && (
                <button style={btn('secondary')} onClick={() => { setActiveStep(1); setVerifyResponse(null); }}>
                  ← Fix permissions and re-verify
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #26262f', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <button style={btn('ghost')} onClick={onClose}>Close</button>

          <div style={{ display: 'flex', gap: '7px' }}>
            {activeStep > 0 && activeStep < 3 && (
              <button style={btn('secondary')} onClick={() => setActiveStep(activeStep - 1)}>← Back</button>
            )}
            {activeStep < 2 && (
              <button style={btn('primary')} onClick={() => setActiveStep(activeStep + 1)}>
                Continue →
              </button>
            )}
            {activeStep === 2 && (
              <button
                style={{ ...btn('primary'), opacity: isBusy ? 0.7 : 1, cursor: isBusy ? 'not-allowed' : 'pointer' }}
                onClick={handleVerify}
                disabled={isBusy}
              >
                {isBusy && <svg style={{ animation: 'spin .8s linear infinite' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/></svg>}
                {isConnecting ? 'Saving…' : isVerifying ? 'Verifying permissions…' : 'Verify & Connect'}
              </button>
            )}
            {activeStep === 3 && verifyResponse?.success && (
              <button style={btn('primary')} onClick={() => { onSuccess?.(); onClose(); }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1.5 6l3.5 3.5 5.5-6"/></svg>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default AWSSetupModal;